import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

/**
 * BRK-1852: minimal create-worksheet dialog (type + ARD). Duplicate
 * (patient, type, ard) rejections surface inline from the backend. The
 * richer anchor UX (ARD/payer-overlap warnings, ARD-change recompute
 * confirm) lands with BRK-1857.
 */

const ASSESSMENT_TYPES = [
  { value: 'PPS_5_DAY', label: '5-Day PPS' },
  { value: 'OBRA_ADMISSION', label: 'OBRA Admission' },
  { value: 'OBRA_QUARTERLY', label: 'OBRA Quarterly' },
  { value: 'OBRA_ANNUAL', label: 'OBRA Annual' },
  { value: 'SIGNIFICANT_CHANGE', label: 'Significant Change' },
  { value: 'IPA', label: 'IPA' },
  { value: 'OBRA_DISCHARGE', label: 'OBRA Discharge' },
];

type Props = {
  open: boolean;
  onClose: () => void;
  onCreate: (assessmentType: string, ard: string) => Promise<void>;
};

const NewWorksheetDialog: React.FC<Props> = ({ open, onClose, onCreate }) => {
  const [assessmentType, setAssessmentType] = useState('OBRA_QUARTERLY');
  const [ard, setArd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // a reopened dialog must not show the previous attempt's error/ARD
  useEffect(() => {
    if (open) {
      setError(null);
      setArd('');
    }
  }, [open]);

  const handleCreate = async () => {
    if (!ard) {
      setError('ARD is required');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onCreate(assessmentType, ard);
      onClose();
    } catch (e: any) {
      setError(e.message || 'Failed to create worksheet');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>New MDS worksheet</DialogTitle>
      <DialogContent>
        <Stack
          gap={2}
          sx={{ mt: 1 }}
        >
          <TextField
            select
            label="Assessment type"
            value={assessmentType}
            onChange={(e) => setAssessmentType(e.target.value)}
            fullWidth
          >
            {ASSESSMENT_TYPES.map((type) => (
              <MenuItem
                key={type.value}
                value={type.value}
              >
                {type.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="ARD (Assessment Reference Date)"
            type="date"
            value={ard}
            onChange={(e) => setArd(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          {error && <Typography color="error">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={submitting}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewWorksheetDialog;
